import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Save,
  Calendar,
  Clock,
  MapPin,
  Euro,
  User,
  Building,
  Briefcase,
  FileText,
  AlertCircle,
  Shield,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { commandeService } from '../services/commandeService';
import qualifications from '../utils/qualifications';

const CommandeModal = ({ 
  isOpen, 
  onClose, 
  commande = null, 
  onSave 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [newPrime, setNewPrime] = useState({ type: '', montant: '' });
  const [selectedDates, setSelectedDates] = useState([]); // Pour vacation et intérim

  const [formData, setFormData] = useState({
    qualification: '',
    typeContrat: '',
    address: '',
    postCode: '',
    town: '',
    startDate: '',
    endDate: '',
    startHour: '',
    endHour: '',
    workedDays: [],
    nbHeureHebdo: '',
    hourlyRate: '',
    quantity: 1,
    task: '',
    justificatif: '',
    isVehicule: false,
    epis: [], // Champ obligatoire
    primes: [], // Champ obligatoire
    consigne: '' // Champ optionnel mais souvent utilisé
  });



  // Initialiser le formulaire
  useEffect(() => {
    if (commande) {
      setFormData({
        qualification: commande.qualification || '',
        typeContrat: commande.typeContrat || '',
        address: commande.address || '',
        postCode: commande.postCode || '',
        town: commande.town || '',
        startDate: commande.startDate ? commande.startDate.split('T')[0] : '',
        endDate: commande.endDate ? commande.endDate.split('T')[0] : '',
        startHour: commande.startHour || '',
        endHour: commande.endHour || '',
        workedDays: commande.workedDays || [],
        nbHeureHebdo: commande.nbHeureHebdo || '',
        hourlyRate: commande.hourlyRate || '',
        quantity: commande.quantity || 1,
        task: commande.task || '',
        justificatif: commande.justificatif || '',
        isVehicule: commande.isVehicule || false
      });

      // Initialiser les dates sélectionnées pour vacation et intérim
      if (commande.selectedDates && Array.isArray(commande.selectedDates)) {
        setSelectedDates(commande.selectedDates);
      } else {
        setSelectedDates([]);
      }
    } else {
      // Reset pour nouvelle commande
      setFormData({
        qualification: '',
        typeContrat: '',
        address: '',
        postCode: '',
        town: '',
        startDate: '',
        endDate: '',
        startHour: '',
        endHour: '',
        workedDays: [],
        nbHeureHebdo: '',
        hourlyRate: '',
        quantity: 1,
        task: '',
        justificatif: '',
        isVehicule: false,
        epis: [],
        primes: [],
        consigne: ''
      });

      // Reset des dates sélectionnées
      setSelectedDates([]);
    }
    setErrors({});
  }, [commande, isOpen]);

  // Gérer les changements de formulaire
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Supprimer l'erreur si le champ est maintenant valide
    if (errors[field] && value) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Gérer l'ajout/suppression d'EPIs
  const toggleEpi = (epi) => {
    setFormData(prev => ({
      ...prev,
      epis: (prev.epis || []).includes(epi)
        ? (prev.epis || []).filter(e => e !== epi)
        : [...(prev.epis || []), epi]
    }));
  };

  // Gérer l'ajout de primes
  const addPrime = () => {
    if (!newPrime.type || !newPrime.montant) return;

    const primeToAdd = {
      prime: newPrime.type,
      primeType: 'fixe',
      taux: newPrime.montant
    };

    setFormData(prev => ({
      ...prev,
      primes: [...prev.primes, primeToAdd]
    }));

    setNewPrime({ type: '', montant: '' });
  };

  // Supprimer une prime
  const removePrime = (index) => {
    setFormData(prev => ({
      ...prev,
      primes: prev.primes.filter((_, i) => i !== index)
    }));
  };

  // Gérer les jours travaillés
  const handleWorkedDaysChange = (day) => {
    setFormData(prev => ({
      ...prev,
      workedDays: (prev.workedDays || []).includes(day)
        ? (prev.workedDays || []).filter(d => d !== day)
        : [...(prev.workedDays || []), day]
    }));
  };

  // Ajouter une nouvelle date
  const addDate = (date) => {
    if (date && selectedDates && !selectedDates.includes(date)) {
      setSelectedDates(prev => [...(prev || []), date].sort());
    }
  };

  // Supprimer une date
  const removeDate = (date) => {
    setSelectedDates(prev => (prev || []).filter(d => d !== date));
  };

  // Valider le formulaire
  const validateForm = () => {
    const newErrors = {};

    if (!formData.qualification.trim()) {
      newErrors.qualification = 'La qualification est obligatoire';
    }
    if (!formData.typeContrat) {
      newErrors.typeContrat = 'Le type de contrat est obligatoire';
    }
    // L'adresse, code postal et ville ne sont plus obligatoires
    // Si non renseignés, l'adresse du siège client sera utilisée par défaut
    // Validation des dates selon le type de contrat
    if (formData.typeContrat === 'CDI') {
      // Placement CDI : pas de date de fin
      if (!formData.startDate) {
        newErrors.startDate = 'La date de début est obligatoire';
      }
    } else if (formData.typeContrat === 'CDD') {
      // Placement CDD : date de début et date de fin
      if (!formData.startDate) {
        newErrors.startDate = 'La date de début est obligatoire';
      }
      if (!formData.endDate) {
        newErrors.endDate = 'La date de fin est obligatoire';
      }
      // Vérifier que la date de fin n'est pas antérieure à la date de début
      if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
        newErrors.endDate = 'La date de fin ne peut pas être antérieure à la date de début';
      }
    } else if (formData.typeContrat === 'Vacation' || formData.typeContrat === 'Intérim') {
      // Vacation et Intérim : sélection de dates multiples
      if (!selectedDates || selectedDates.length === 0) {
        newErrors.selectedDates = 'Au moins une date doit être sélectionnée';
      }
    }
    if (!formData.startHour) {
      newErrors.startHour = 'L\'heure de début est obligatoire';
    }
    if (!formData.endHour) {
      newErrors.endHour = 'L\'heure de fin est obligatoire';
    }
    if ((formData.workedDays || []).length === 0) {
      newErrors.workedDays = 'Au moins un jour travaillé est obligatoire';
    }
    if (!formData.nbHeureHebdo) {
      newErrors.nbHeureHebdo = 'Le nombre d\'heures hebdomadaires est obligatoire';
    }
    if (!formData.hourlyRate) {
      newErrors.hourlyRate = 'Le taux horaire est obligatoire';
    }
    if (!formData.task.trim()) {
      newErrors.task = 'La description des tâches est obligatoire';
    }
    if (!formData.justificatif.trim()) {
      newErrors.justificatif = 'Le justificatif est obligatoire';
    }
    if ((formData.epis || []).length === 0) {
      newErrors.epis = 'Au moins un EPI est obligatoire';
    }
    if ((formData.primes || []).length === 0) {
      newErrors.primes = 'Au moins une prime est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Sauvegarder la commande
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setIsLoading(true);
    try {
      let savedCommande;

      // Préparer les données selon le type de contrat
      const dataToSend = { ...formData };

      if (formData.typeContrat === 'CDI') {
        // Placement CDI : pas de date de fin
        delete dataToSend.endDate;
      } else if (formData.typeContrat === 'Vacation' || formData.typeContrat === 'Intérim') {
        // Vacation et Intérim : utiliser les dates sélectionnées
        dataToSend.selectedDates = selectedDates;
        delete dataToSend.startDate;
        delete dataToSend.endDate;
      }

      if (commande) {
        // Modification - les infos client seront récupérées automatiquement côté backend
        savedCommande = await commandeService.updateCommande(commande._id, dataToSend);
        toast.success('Commande modifiée avec succès');
      } else {
        // Création - les infos client seront récupérées automatiquement côté backend
        savedCommande = await commandeService.createCommande(dataToSend);
        toast.success('Commande créée avec succès');
      }

      if (onSave) {
        onSave(savedCommande);
      }

      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde de la commande');
    } finally {
      setIsLoading(false);
    }
  };

  const jours = [
    { value: 'Lundi', label: 'Lundi' },
    { value: 'Mardi', label: 'Mardi' },
    { value: 'Mercredi', label: 'Mercredi' },
    { value: 'Jeudi', label: 'Jeudi' },
    { value: 'Vendredi', label: 'Vendredi' },
    { value: 'Samedi', label: 'Samedi' },
    { value: 'Dimanche', label: 'Dimanche' }
  ];

  const typesContrat = [
    { value: 'CDI', label: 'Placement CDI' },
    { value: 'CDD', label: 'Placement CDD' },
    { value: 'Intérim', label: 'Intérim' },
    { value: 'Vacation', label: 'Vacation' },
  ];

  const episOptions = [
    'Casque de sécurité',
    'Lunettes de protection',
    'Gants de protection',
    'Chaussures de sécurité',
    'Gilet haute visibilité',
    'Masque respiratoire',
    'Harnais de sécurité',
    'Bouchons d\'oreilles',
    'Combinaison de protection',
    'Genouillères',
    'Ceinture lombaire',
    'Écran facial'
  ];

  const primesOptions = [
    'Prime 13e mois',
    'Prime d\'assiduité',
    'Prime d\'ancienneté',
    'Prime d\'astreinte',
    'Prime d\'encadrement',
    'Prime de fonction',
    'Prime de salissure',
    'Prime de pénibilité',
    'Prime de performance',
    'Prime de productivité',
    'Prime de nuit',
    'Prime de panier',
    'Prime de ponctualité',
    'Prime de froid',
    'Prime de déplacement',
    'Prime exceptionnelle'
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
        >
          {/* En-tête */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Briefcase size={24} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-800">
                  {commande ? 'Modifier la commande' : 'Nouvelle commande'}
                </h2>
                <p className="text-sm text-slate-600">
                  {commande ? 'Modifiez les informations de la commande' : 'Créez une nouvelle commande de recrutement'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Contenu scrollable */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="p-6 pb-32 space-y-6">
              {/* Informations générales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Briefcase size={16} className="inline mr-2" />
                    Qualification *
                  </label>
                  <input
                    type="text"
                    list="qualifications-list"
                    value={formData.qualification}
                    onChange={(e) => handleInputChange('qualification', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.qualification ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="Tapez ou sélectionnez une qualification"
                  />
                  <datalist id="qualifications-list">
                    {qualifications.map(qualification => (
                      <option key={qualification} value={qualification} />
                    ))}
                  </datalist>
                  {errors.qualification && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.qualification}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <FileText size={16} className="inline mr-2" />
                    Type de contrat *
                  </label>
                  <select
                    value={formData.typeContrat}
                    onChange={(e) => handleInputChange('typeContrat', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.typeContrat ? 'border-red-300' : 'border-slate-300'
                    }`}
                  >
                    <option value="">Sélectionner un type</option>
                    {typesContrat.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {errors.typeContrat && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.typeContrat}
                    </p>
                  )}
                </div>
              </div>

              {/* Adresse */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <MapPin size={16} className="inline mr-2" />
                    Adresse de mission (si differente du siège)
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.address ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="Adresse complète"
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.address}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Code postal 
                  </label>
                  <input
                    type="text"
                    value={formData.postCode}
                    onChange={(e) => handleInputChange('postCode', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.postCode ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="75001"
                  />
                  {errors.postCode && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.postCode}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ville 
                </label>
                <input
                  type="text"
                  value={formData.town}
                  onChange={(e) => handleInputChange('town', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.town ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Paris"
                />
                {errors.town && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.town}
                  </p>
                )}
              </div>

              {/* Dates - Affichage conditionnel selon le type de contrat */}
              {formData.typeContrat === 'CDI' && (
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <Calendar size={16} className="inline mr-2" />
                      Date de début *
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.startDate ? 'border-red-300' : 'border-slate-300'
                      }`}
                    />
                    {errors.startDate && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.startDate}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-slate-500">
                      Placement CDI - Pas de date de fin requise
                    </p>
                  </div>
                </div>
              )}

              {formData.typeContrat === 'CDD' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <Calendar size={16} className="inline mr-2" />
                      Date de début *
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.startDate ? 'border-red-300' : 'border-slate-300'
                      }`}
                    />
                    {errors.startDate && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.startDate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <Calendar size={16} className="inline mr-2" />
                      Date de fin *
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      min={formData.startDate || undefined}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.endDate ? 'border-red-300' : 'border-slate-300'
                      }`}
                    />
                    {errors.endDate && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.endDate}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {(formData.typeContrat === 'Vacation' || formData.typeContrat === 'Intérim') && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <Calendar size={16} className="inline mr-2" />
                      Sélection des dates *
                    </label>
                    <p className="text-sm text-slate-500 mb-3">
                      {formData.typeContrat === 'Vacation' ? 'Sélectionnez les dates de vacation' : 'Sélectionnez les dates d\'intérim'}
                    </p>

                    <div className="flex gap-2 mb-3">
                      <input
                        type="date"
                        id="new-date-input"
                        className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => {
                          if (e.target.value) {
                            addDate(e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.getElementById('new-date-input');
                          if (input.value) {
                            addDate(input.value);
                            input.value = '';
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Plus size={16} />
                      </button>
                    </div>

                    {selectedDates && selectedDates.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-700">Dates sélectionnées :</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedDates.map(date => (
                            <div
                              key={date}
                              className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                            >
                              <span>{new Date(date + 'T00:00:00').toLocaleDateString('fr-FR')}</span>
                              <button
                                type="button"
                                onClick={() => removeDate(date)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {errors.selectedDates && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.selectedDates}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Horaires */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Clock size={16} className="inline mr-2" />
                    Heure de début *
                  </label>
                  <input
                    type="time"
                    value={formData.startHour}
                    onChange={(e) => handleInputChange('startHour', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.startHour ? 'border-red-300' : 'border-slate-300'
                    }`}
                  />
                  {errors.startHour && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.startHour}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Clock size={16} className="inline mr-2" />
                    Heure de fin *
                  </label>
                  <input
                    type="time"
                    value={formData.endHour}
                    onChange={(e) => handleInputChange('endHour', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.endHour ? 'border-red-300' : 'border-slate-300'
                    }`}
                  />
                  {errors.endHour && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.endHour}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Heures hebdo *
                  </label>
                  <input
                    type="number"
                    value={formData.nbHeureHebdo}
                    onChange={(e) => handleInputChange('nbHeureHebdo', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.nbHeureHebdo ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="35"
                  />
                  {errors.nbHeureHebdo && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.nbHeureHebdo}
                    </p>
                  )}
                </div>
              </div>

              {/* Jours travaillés */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Jours travaillés *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                  {jours.map(jour => (
                    <label key={jour.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(formData.workedDays || []).includes(jour.value)}
                        onChange={() => handleWorkedDaysChange(jour.value)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">{jour.label}</span>
                    </label>
                  ))}
                </div>
                {errors.workedDays && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.workedDays}
                  </p>
                )}
              </div>

              {/* Taux et quantité */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Euro size={16} className="inline mr-2" />
                    Taux horaire *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.hourlyRate}
                    onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.hourlyRate ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="15.50"
                  />
                  {errors.hourlyRate && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.hourlyRate}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Quantité
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="1"
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description des tâches *
                </label>
                <textarea
                  value={formData.task}
                  onChange={(e) => handleInputChange('task', e.target.value)}
                  rows={4}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                    errors.task ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Décrivez les tâches et missions à effectuer..."
                />
                {errors.task && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.task}
                  </p>
                )}
              </div>

              {/* EPIs */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  <Shield size={16} className="inline mr-2" />
                  Équipements de Protection Individuelle (EPI) *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {episOptions.map(epi => (
                    <label key={epi} className="flex items-center space-x-2 cursor-pointer p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={(formData.epis || []).includes(epi)}
                        onChange={() => toggleEpi(epi)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">{epi}</span>
                    </label>
                  ))}
                </div>
                {errors.epis && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.epis}
                  </p>
                )}
                {(formData.epis || []).length === 0 && !errors.epis && (
                  <p className="mt-2 text-sm text-slate-500">
                    Sélectionnez au moins un EPI ou cochez "Aucun EPI requis"
                  </p>
                )}
                <label className="flex items-center space-x-2 cursor-pointer mt-3 p-2 rounded-lg border border-slate-200 bg-slate-50">
                  <input
                    type="checkbox"
                    checked={(formData.epis || []).includes('Aucun EPI requis')}
                    onChange={() => toggleEpi('Aucun EPI requis')}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Aucun EPI requis</span>
                </label>
              </div>

              {/* Primes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  <Euro size={16} className="inline mr-2" />
                  Primes *
                </label>

                {/* Liste des primes ajoutées */}
                {(formData.primes || []).length > 0 && (
                  <div className="mb-4 space-y-2">
                    {formData.primes.map((prime, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex-1">
                          <span className="font-medium text-slate-700">{prime.prime}</span>
                          <span className="ml-2 text-slate-500">- {prime.taux}€</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePrime(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Formulaire d'ajout de prime */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 border border-slate-200 rounded-lg bg-slate-50">
                  <div>
                    <select
                      value={newPrime.type}
                      onChange={(e) => setNewPrime(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sélectionner une prime</option>
                      {primesOptions.map(prime => (
                        <option key={prime} value={prime}>{prime}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <input
                      type="number"
                      step="0.01"
                      value={newPrime.montant}
                      onChange={(e) => setNewPrime(prev => ({ ...prev, montant: e.target.value }))}
                      placeholder="Montant (€)"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={addPrime}
                      disabled={!newPrime.type || !newPrime.montant}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <Plus size={16} className="mr-1" />
                      Ajouter
                    </button>
                  </div>
                </div>

                {errors.primes && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.primes}
                  </p>
                )}
                {(formData.primes || []).length === 0 && !errors.primes && (
                  <p className="mt-2 text-sm text-slate-500">
                    Ajoutez au moins une prime ou cochez "Aucune prime"
                  </p>
                )}

                <label className="flex items-center space-x-2 cursor-pointer mt-3 p-2 rounded-lg border border-slate-200 bg-slate-50">
                  <input
                    type="checkbox"
                    checked={formData.primes?.some(p => p.prime === 'Aucune prime')}
                    onChange={() => {
                      if (formData.primes?.some(p => p.prime === 'Aucune prime')) {
                        setFormData(prev => ({
                          ...prev,
                          primes: prev.primes.filter(p => p.prime !== 'Aucune prime')
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          primes: [{ prime: 'Aucune prime', primeType: 'fixe', taux: '0' }]
                        }));
                      }
                    }}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Aucune prime</span>
                </label>
              </div>

              {/* Consignes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <FileText size={16} className="inline mr-2" />
                  Consignes particulières
                </label>
                <textarea
                  value={formData.consigne}
                  onChange={(e) => handleInputChange('consigne', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Consignes particulières pour cette mission..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Justificatif *
                </label>
                <textarea
                  value={formData.justificatif}
                  onChange={(e) => handleInputChange('justificatif', e.target.value)}
                  rows={4}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                    errors.justificatif ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Justification du besoin..."
                />
                {errors.justificatif && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.justificatif}
                  </p>
                )}
              </div>

              {/* Options */}
              <div>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isVehicule}
                    onChange={(e) => handleInputChange('isVehicule', e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Véhicule requis</span>
                </label>
              </div>
            </div>

             {/* Pied de page */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-slate-200 bg-slate-50">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>{commande ? 'Modifier' : 'Créer'}</span>
                </>
              )}
            </button>
          </div>
          </div>

         
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CommandeModal;
